import { Inject, Injectable } from '@angular/core';
import { Auth } from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { collectionData } from 'rxfire/firestore';
import { AUTH, FIRESTORE } from '../firebase-injection';
import { firstValueFrom, Observable, Subscriber } from 'rxjs';
import { Deck, deckConverter, DeckId } from '../models/deck.model';
import { Flashcard, flashcardConverter } from '../models/flashcard.model';
import { UserId } from '../models/user.model';
import {
  PrepareModeReviewService,
  StudyModeReviewService,
} from './review.service';
import { SpacedRepetitionService } from './spaced-repetition.service';

@Injectable({
  providedIn: 'root',
})
export class DeckService {
  constructor(
    @Inject(FIRESTORE) private firestore: Firestore,
    private prepareModeReviewService: PrepareModeReviewService,
    private studyModeReviewService: StudyModeReviewService,
    private spacedRepetitionService: SpacedRepetitionService,
    @Inject(AUTH) private auth: Auth
  ) {}

  public setDeckForUser(
    userId: UserId,
    deckId: DeckId | undefined,
    deck: Deck
  ): Promise<DeckId> {
    let deckRef: DocumentReference<Deck | undefined>;
    if (!deckId) {
      deckRef = doc(
        collection(this.firestore, 'users', userId, 'decks')
      ).withConverter(deckConverter);
    } else {
      deckRef = doc(
        this.firestore,
        'users',
        userId,
        'decks',
        deckId
      ).withConverter(deckConverter);
    }
    return setDoc(deckRef, deck).then(() => {
      return deckRef.id;
    });
  }

  updateDeckOfUser(userId: UserId, deckId: DeckId, deck: Deck) {
    const deckRef = doc(
      this.firestore,
      'users',
      userId,
      'decks',
      deckId
    ).withConverter(deckConverter);
    return setDoc(deckRef, deck).then(() => {
      return deck;
    });
  }

  markDeckAsStudied(userId: UserId, deck: Deck) {
    deck.lastStudiedAt = Timestamp.now();
    return this.updateDeckOfUser(userId, deck.id, deck);
  }

  /**
   * Increments the number of cards in a deck by n, for decrementing use a negative n
   * @param userId
   * @param deckId
   * @param n: amount to increment by
   * @returns: a promise that resolves when the operation is complete
   */
  public incrementDeckNumberOfCards(
    userId: UserId,
    deckId: DeckId,
    deck?: Deck,
    n = 1
  ) {
    console.log('incrementDeckNumberOfCards', userId, deckId, n);
    if (!deck) {
      return firstValueFrom(this.getDeckOfUser(userId, deckId)).then(
        (_deck) => {
          if (!_deck) {
            return;
          }
          _deck!.numberOfCards = _deck.numberOfCards
            ? _deck!.numberOfCards + n
            : n;
          _deck!.numberOfDueCardsInStudyMode = _deck.numberOfDueCardsInStudyMode
            ? _deck!.numberOfDueCardsInStudyMode + n
            : n;
          _deck!.numberOfDueCardsInPrepareMode =
            _deck.numberOfDueCardsInPrepareMode
              ? _deck!.numberOfDueCardsInPrepareMode + n
              : n;
          _deck!.lastPrepareModeDueCardsLeft = 0;
          _deck!.lastStudyModeDueCardsLeft = 0;
          _deck!.lastVerifyModeDueCardsLeft = 0;
          console.log('deck after incrementing', _deck);
          return this.updateDeckOfUser(userId, deckId, _deck);
        }
      );
    }
    deck!.numberOfCards = deck.numberOfCards ? deck!.numberOfCards + n : n;
    deck!.numberOfDueCardsInStudyMode = deck.numberOfDueCardsInStudyMode
      ? deck!.numberOfDueCardsInStudyMode + n
      : n;
    deck!.numberOfDueCardsInPrepareMode = deck.numberOfDueCardsInPrepareMode
      ? deck!.numberOfDueCardsInPrepareMode + n
      : n;
    deck!.lastPrepareModeDueCardsLeft = 0;
    deck!.lastStudyModeDueCardsLeft = 0;
    deck!.lastVerifyModeDueCardsLeft = 0;
    return this.updateDeckOfUser(userId, deckId, deck);
  }

  /**
   * Increments the number of due cards for prepare mode in a deck by n, for decrementing use a negative n
   * See @see DeckService.incrementDeckNumberOfCards
   * @param userId
   * @param deckId
   * @param n amount to increment by
   * @returns a promise that resolves when the operation is complete
   */
  public incrementDeckNumberOFDueCardsInPrepareMode(
    userId: UserId,
    deckId: DeckId,
    n = 1
  ) {
    return this.incrementDeckField(
      userId,
      deckId,
      'numberOFDueCardsInPrepareMode',
      n
    );
  }

  public incrementDeckNumberOFDueCardsInStudyMode(
    userId: UserId,
    deckId: DeckId,
    n = 1
  ) {
    return this.incrementDeckField(
      userId,
      deckId,
      'numberOFDueCardsInStudyMode',
      n
    );
  }

  private incrementDeckField(
    userId: UserId,
    deckId: DeckId,
    field: string,
    n = 1
  ) {
    const deckRef = doc(
      this.firestore,
      'users',
      userId,
      'decks',
      deckId
    ).withConverter(deckConverter);
    return updateDoc(deckRef, field, increment(n));
  }

  public getDeckOfUser(userId: UserId, deckId: DeckId) {
    const deckRef = doc(
      this.firestore,
      'users',
      userId,
      'decks',
      deckId
    ).withConverter(deckConverter);
    return new Observable((subscriber: Subscriber<Deck | undefined>) => {
      subscriber.unsubscribe = onSnapshot(
        deckRef,
        (doc) => {
          subscriber.next(doc.data());
        },
        (error) => {
          subscriber.error(error);
        }
      );
    });
  }

  public async checkIfDeckExists(
    userId: string,
    deckName: string
  ): Promise<boolean> {
    const decksQuery = query(
      collection(this.firestore, 'users', userId, 'decks').withConverter(
        deckConverter
      )
    );

    try {
      const querySnapshot = await getDocs(decksQuery);
      for (const doc of querySnapshot.docs) {
        if (doc.data()?.name === deckName) {
          return true;
        }
      }
      return false;
    } catch (error) {
      throw new Error(
        `Failed to check if deck exists: ${(error as Error).message}`
      );
    }
  }

  public getDecksOfUser(userId: UserId) {
    const decksCol = collection(
      this.firestore,
      'users',
      userId,
      'decks'
    ).withConverter(deckConverter);
    return collectionData(decksCol) as Observable<Deck[]>;
  }

  deleteDeck(userId: UserId, deckId: DeckId) {
    const deckRef = doc(this.firestore, 'users', userId, 'decks', deckId);
    return deleteDoc(deckRef);
    // this doesn't delete existing subcollections
    // but there is an firebase function which is triggered onDelete to do that
  }

  updateDueCardsOfDeckOfUser(
    userId: UserId,
    deck: Deck,
    forceUpdate = false,
    resetLastDueCardsLeft = false
  ) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (
      !forceUpdate &&
      !resetLastDueCardsLeft &&
      deck.numberOfDueCardsUpdatedTimestamp &&
      deck.numberOfDueCardsUpdatedTimestamp.toDate() > yesterday
    ) {
      return Promise.resolve(undefined);
    }
    let deckChanged = false;

    const flashcardsRef = collection(
      this.firestore,
      'users',
      userId,
      'decks',
      deck.id,
      'flashcards'
    ).withConverter(flashcardConverter);

    return getDocs(flashcardsRef)
      .then((flashcardQuerySnapshots) => {
        if (flashcardQuerySnapshots.empty) {
          if (deck.numberOfCards !== 0) {
            deck.numberOfCards = 0;
            deck.numberOfDueCardsInPrepareMode = 0;
            deck.numberOfDueCardsInStudyMode = 0;
            deckChanged = true;
          }
          return;
        }
        const flashcards = <Flashcard[]>flashcardQuerySnapshots.docs
          .map((doc) => {
            return doc.data();
          })
          .filter((fc) => fc);
        if (deck.numberOfCards !== flashcards.length) {
          deck.numberOfCards = flashcards.length;
          deck.studiedInPrepareMode = false;
          deckChanged = true;
        }
        return Promise.all([
          firstValueFrom(
            this.prepareModeReviewService.getReviewsOfDeck(userId, deck.id)
          ).then((prepareReviews) => {
            if (!prepareReviews || prepareReviews.length === 0) {
              if (deck.numberOfDueCardsInPrepareMode !== flashcards.length) {
                deck.numberOfDueCardsInPrepareMode = flashcards.length;
                deckChanged = true;
              }
              return;
            }
            const numberOfDueCards =
              this.spacedRepetitionService.getDueFlashcards(
                flashcards,
                prepareReviews
              ).length;

            if (deck.numberOfDueCardsInPrepareMode !== numberOfDueCards) {
              deck.numberOfDueCardsInPrepareMode = numberOfDueCards;
              deckChanged = true;
            }
          }),
          firstValueFrom(
            this.studyModeReviewService.getReviewsOfDeck(userId, deck.id)
          ).then((studyReviews) => {
            if (!studyReviews || studyReviews.length === 0) {
              if (deck.numberOfDueCardsInStudyMode !== flashcards.length) {
                deck.numberOfDueCardsInStudyMode = flashcards.length;
                deckChanged = true;
              }
              return;
            }
            const numberOfDueCards =
              this.spacedRepetitionService.getDueFlashcards(
                flashcards,
                studyReviews
              ).length;

            if (deck.numberOfDueCardsInStudyMode !== numberOfDueCards) {
              deck.numberOfDueCardsInStudyMode = numberOfDueCards;
              deckChanged = true;
            }
          }),
        ]);
      })
      .then(() => {
        if (resetLastDueCardsLeft) {
          deck.lastPrepareModeDueCardsLeft = 0;
          deck.lastStudyModeDueCardsLeft = 0;
          deck.lastVerifyModeDueCardsLeft = 0;
          deckChanged = true;
        }
        if (deckChanged) {
          deck.numberOfDueCardsUpdatedTimestamp = Timestamp.now();
          this.updateDeckOfUser(userId, deck.id, deck);
        }
      })
      .then(() => {
        return deckChanged ? deck : undefined;
      });
  }

  getPublicDeck(id: DeckId) {
    return getDoc(
      doc(this.firestore, 'public-decks', id).withConverter(deckConverter)
    ).then((doc) => {
      return doc.data();
    });
  }

  addDeckToPublicDecks(
    deckId: string | undefined,
    deck: Deck,
    copyFlashcards = false
  ): Promise<DeckId> {
    if (!deckId) {
      return Promise.reject('deckId is required');
    }
    if (
      !this.auth.currentUser ||
      this.auth.currentUser.uid !== deck.author?.id
    ) {
      return Promise.reject('User is not authorized to write this deck');
    }

    const deckRef = doc(this.firestore, 'public-decks', deckId).withConverter(
      deckConverter
    );
    return setDoc(deckRef, deck).then(() => {
      if (copyFlashcards) {
        return this.copyFlashcardsToPublicDeck(deck.author!.id, deckId).then(
          () => {
            return deckRef.id;
          }
        );
      }
      return Promise.resolve(deckRef.id);
    });
  }

  private copyFlashcardsToPublicDeck(authorId: UserId, deckId: DeckId) {
    const flashcardsRef = collection(
      this.firestore,
      'users',
      authorId,
      'decks',
      deckId,
      'flashcards'
    ).withConverter(flashcardConverter);
    return getDocs(flashcardsRef).then((flashcardQuerySnapshots) => {
      const flashcards = <Flashcard[]>flashcardQuerySnapshots.docs
        .map((doc) => {
          return doc.data();
        })
        .filter((fc) => fc);
      return Promise.all(
        flashcards.map((flashcard) => {
          const publicFlashcardRef = doc(
            this.firestore,
            'public-decks',
            deckId,
            'flashcards',
            flashcard.id
          ).withConverter(flashcardConverter);
          return setDoc(publicFlashcardRef, flashcard);
        })
      ).then(() => {
        return;
      });
    });
  }

  deletePublicDeck(id: DeckId) {
    const deckRef = doc(this.firestore, 'public-decks', id);
    return deleteDoc(deckRef);
  }
}
